import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle, Send, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '+91 8977116677',
      link: 'tel:+918977116677',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'support@dharma-astra.org',
      link: 'mailto:support@dharma-astra.org',
    },
    {
      icon: MapPin,
      title: 'Address',
      value: 'Hyderabad, Telangana, India',
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="contact-page">
      {/* Hero */}
      <section className="relative bg-terracotta py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Get In Touch</span>
            <h1 className="font-heading text-4xl md:text-5xl text-white mt-4 mb-4">
              Contact Us
            </h1>
            <p className="font-body text-lg text-white/80">
              Have questions or want to learn more about our work? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="bg-white rounded-2xl border-none shadow-sm">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-saffron/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <info.icon className="w-6 h-6 text-saffron" />
                      </div>
                      <h4 className="font-heading text-sm text-gray-500 mb-1">{info.title}</h4>
                      {info.link ? (
                        <a href={info.link} className="font-body text-sm text-gray-900 hover:text-saffron transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-body text-sm text-gray-900">{info.value}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Google Map */}
              <Card className="bg-white rounded-2xl border-none shadow-sm overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243647.3169144165!2d78.26761281913052!3d17.412281015498517!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99daeaebd2c7%3A0xae93b78392bafbc2!2sHyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1703000000000!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location Map"
                  className="rounded-2xl"
                ></iframe>
              </Card>

              {/* WhatsApp Button */}
              <a
                href="https://wa.me/918977116677?text=Hello%20Dharma%20Astra%20Association"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-full py-4 px-8 font-body font-semibold transition-colors shadow-lg"
                data-testid="whatsapp-button"
              >
                <MessageCircle className="w-6 h-6" />
                Chat on WhatsApp
              </a>
            </div>

            {/* Contact Form */}
            <Card className="bg-white rounded-2xl border-none shadow-lg">
              <CardContent className="p-8 md:p-10">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-2xl text-gray-900 mb-4">Message Sent!</h3>
                    <p className="font-body text-gray-600 mb-6">
                      Thank you for reaching out. We'll get back to you soon.
                    </p>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                      }}
                      className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-6 font-body"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-saffron/10 rounded-full flex items-center justify-center">
                        <Send className="w-6 h-6 text-saffron" />
                      </div>
                      <div>
                        <h2 className="font-heading text-2xl text-gray-900">Send a Message</h2>
                        <p className="font-body text-sm text-gray-500">We'll respond within 24 hours</p>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <Label className="font-body text-sm text-gray-700">Name *</Label>
                          <Input
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your name"
                            className={`mt-1 py-3 rounded-lg font-body ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
                            data-testid="input-contact-name"
                          />
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        <div>
                          <Label className="font-body text-sm text-gray-700">Phone *</Label>
                          <Input
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Phone number"
                            className={`mt-1 py-3 rounded-lg font-body ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                            data-testid="input-contact-phone"
                          />
                          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>
                      </div>

                      <div>
                        <Label className="font-body text-sm text-gray-700">Email *</Label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          className={`mt-1 py-3 rounded-lg font-body ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                          data-testid="input-contact-email"
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>

                      <div>
                        <Label className="font-body text-sm text-gray-700">Subject *</Label>
                        <Input
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="What is this about?"
                          className={`mt-1 py-3 rounded-lg font-body ${errors.subject ? 'border-red-500' : 'border-gray-200'}`}
                          data-testid="input-contact-subject"
                        />
                        {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                      </div>

                      <div>
                        <Label className="font-body text-sm text-gray-700">Message *</Label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Write your message here..."
                          rows={5}
                          className={`mt-1 rounded-lg font-body resize-none ${errors.message ? 'border-red-500' : 'border-gray-200'}`}
                          data-testid="input-contact-message"
                        />
                        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-saffron hover:bg-saffron-dark text-white rounded-full py-6 font-body font-semibold text-lg btn-lift disabled:opacity-50"
                        data-testid="contact-submit-btn"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Send Message
                          </span>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/918977116677?text=Hello%20Dharma%20Astra%20Association"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
};

export default ContactPage;
